import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './api';

const CurrencyCtx = createContext(null);

const SYMBOL = { EUR: '€', USD: '$', GBP: '£', CHF: 'Fr ', AED: 'AED ' };
const STORAGE_KEY = 'harizi_currency_v1';

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'EUR'; } catch { return 'EUR'; }
  });
  const [rates, setRates] = useState({ EUR: 1 });
  const [supported, setSupported] = useState(['EUR', 'USD', 'GBP', 'CHF', 'AED']);

  useEffect(() => {
    let mounted = true;
    api.get('/currency/rates').then(({ data }) => {
      if (!mounted) return;
      setRates(data.rates || { EUR: 1 });
      setSupported(data.supported || supported);
    }).catch(() => {});
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c);
    try { localStorage.setItem(STORAGE_KEY, c); } catch {}
  }, []);

  const convert = useCallback((amountEUR) => {
    const r = rates[currency] || 1;
    return amountEUR * r;
  }, [rates, currency]);

  const format = useCallback((amountEUR, opts = {}) => {
    const converted = convert(amountEUR);
    const sym = SYMBOL[currency] || `${currency} `;
    const locale = currency === 'EUR' ? 'it-IT' : currency === 'AED' ? 'en-US' : 'en-GB';
    const fixed = (opts.decimals ?? 0);
    const formatted = new Intl.NumberFormat(locale, { minimumFractionDigits: fixed, maximumFractionDigits: fixed }).format(converted);
    return `${sym}${formatted}`;
  }, [convert, currency]);

  return (
    <CurrencyCtx.Provider value={{ currency, setCurrency, rates, supported, convert, format }}>
      {children}
    </CurrencyCtx.Provider>
  );
}

export const useCurrency = () => {
  const ctx = useContext(CurrencyCtx);
  if (!ctx) throw new Error('useCurrency must be used inside CurrencyProvider');
  return ctx;
};
